import { StudioError, type Project, type StudioBackend } from "./backend.js";
import type { ImageStore } from "./images.js";
import { normalizeDoc, type StudioDoc } from "./model.js";

/**
 * One open project: its document, its version, and the saving. Every change is written at
 * once to a local draft (this browser), then saved to the shared store after a short pause.
 * A save names the version it started from: when the other member saved in the meantime,
 * the store refuses (conflict) and nothing is overwritten; the changes stay here and in the
 * draft until the member decides (copy, project file, or the other version).
 */
export type SaveState = "saved" | "unsaved" | "saving" | "error" | "conflict";

export interface Draft {
  projectId: string;
  baseVersion: number;
  name: string;
  doc: unknown;
  at: string;
}

const DRAFT_PREFIX = "opulancia-atelier-brouillon:";
const RETRY = [4000, 10000, 30000];

export function readDraft(projectId: string, storage: Storage | null = safeStorage()): Draft | null {
  try {
    const raw = storage?.getItem(DRAFT_PREFIX + projectId);
    if (!raw) return null;
    const draft = JSON.parse(raw) as Draft;
    return draft && draft.projectId === projectId && typeof draft.baseVersion === "number" ? draft : null;
  } catch {
    return null;
  }
}

export function draftIds(storage: Storage | null = safeStorage()): Set<string> {
  const ids = new Set<string>();
  try {
    for (let i = 0; storage && i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(DRAFT_PREFIX)) ids.add(key.slice(DRAFT_PREFIX.length));
    }
  } catch {
    /* storage unavailable: no drafts to show */
  }
  return ids;
}

export function discardDraft(projectId: string, storage: Storage | null = safeStorage()): void {
  try {
    storage?.removeItem(DRAFT_PREFIX + projectId);
  } catch {
    /* nothing to remove */
  }
}

function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export interface SessionOptions {
  /** Pause after the last change before saving (ms). */
  delay?: number;
  storage?: Storage | null;
  onState?: (session: ProjectSession) => void;
}

export class ProjectSession {
  readonly id: string;
  doc: StudioDoc;
  name: string;
  version: number;
  updatedBy: string;
  updatedAt: string;
  state: SaveState = "saved";
  /** Why the last save failed, or what the conflict is about. */
  message = "";
  /** The version stored elsewhere, when there is a conflict. */
  remoteVersion: number | null = null;
  /** False when this browser refused the local draft (private mode, full storage). */
  draftOk = true;
  nextRetry: number | null = null;

  private revision = 0;
  private savedRevision = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running: Promise<boolean> | null = null;
  private attempts = 0;
  private readonly delay: number;
  private readonly storage: Storage | null;
  private readonly onState: (session: ProjectSession) => void;
  private readonly online = () => {
    if (this.state === "error") void this.save();
  };

  constructor(
    private readonly backend: StudioBackend,
    private readonly images: ImageStore,
    project: Project,
    options: SessionOptions = {},
  ) {
    this.id = project.id;
    this.doc = normalizeDoc(project.doc);
    this.name = project.name;
    this.version = project.version;
    this.updatedBy = project.updated_by;
    this.updatedAt = project.updated_at;
    this.delay = options.delay ?? 1200;
    this.storage = options.storage === undefined ? safeStorage() : options.storage;
    this.onState = options.onState ?? (() => {});
    if (typeof window !== "undefined") window.addEventListener("online", this.online);
  }

  get dirty(): boolean {
    return this.revision !== this.savedRevision;
  }

  private set(state: SaveState, message = ""): void {
    this.state = state;
    this.message = message;
    this.onState(this);
  }

  /** Records a change made to `doc` or `name`: local draft now, shared save after the pause. */
  changed(): void {
    this.revision += 1;
    this.writeDraft();
    if (this.state === "conflict") return;
    if (this.state !== "saving") this.set(this.state === "error" ? "error" : "unsaved", this.message);
    this.schedule(this.delay);
  }

  /** Starts from a draft kept in this browser (same base version as the stored project). */
  restore(draft: Draft): void {
    this.doc = normalizeDoc(draft.doc);
    this.name = draft.name;
    this.changed();
  }

  private writeDraft(): void {
    const draft: Draft = { projectId: this.id, baseVersion: this.version, name: this.name, doc: this.doc, at: new Date().toISOString() };
    try {
      this.storage?.setItem(DRAFT_PREFIX + this.id, JSON.stringify(draft));
      this.draftOk = !!this.storage;
    } catch {
      this.draftOk = false;
    }
  }

  private schedule(ms: number): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.save();
    }, ms);
  }

  /** Saves now (waits for a save already running). True when everything is saved. */
  save(): Promise<boolean> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.state === "conflict") return Promise.resolve(false);
    if (this.running) return this.running.then(() => (this.dirty ? this.save() : true));
    if (!this.dirty) return Promise.resolve(true);
    this.running = this.run().finally(() => (this.running = null));
    return this.running;
  }

  private async run(): Promise<boolean> {
    const revision = this.revision;
    const doc = structuredClone(this.doc);
    const name = this.name;
    this.nextRetry = null;
    this.set("saving");
    try {
      // the images first: a saved project never points to a file that is not stored
      await this.images.flush();
      const saved = await this.backend.saveProject(this.id, this.version, name, doc);
      this.version = saved.version;
      this.updatedBy = saved.updated_by;
      this.updatedAt = saved.updated_at;
      this.savedRevision = revision;
      this.attempts = 0;
      if (this.dirty) {
        this.writeDraft(); // the draft now builds on the new version
        this.set("unsaved");
        this.schedule(this.delay);
        return false;
      }
      discardDraft(this.id, this.storage);
      this.set("saved");
      return true;
    } catch (error) {
      if (error instanceof StudioError && error.code === "conflict") {
        this.remoteVersion = error.remoteVersion ?? null;
        this.set("conflict", "Le projet a été enregistré par l'autre membre pendant tes modifications.");
        return false;
      }
      const message = error instanceof Error ? error.message : String(error);
      if (error instanceof StudioError && ["archived", "not_found", "forbidden", "invalid"].includes(error.code)) {
        this.set("error", message);
        return false;
      }
      const wait = RETRY[Math.min(this.attempts, RETRY.length - 1)]!;
      this.attempts += 1;
      this.nextRetry = Date.now() + wait;
      this.schedule(wait);
      this.set("error", message);
      return false;
    }
  }

  /** After a conflict, or to give up local changes: the stored version replaces this one. */
  async reload(): Promise<void> {
    const project = await this.backend.getProject(this.id);
    this.doc = normalizeDoc(project.doc);
    this.name = project.name;
    this.version = project.version;
    this.updatedBy = project.updated_by;
    this.updatedAt = project.updated_at;
    this.savedRevision = this.revision;
    this.remoteVersion = null;
    discardDraft(this.id, this.storage);
    this.set("saved");
  }

  /** The draft is cleared once its content lives elsewhere (saved as a copy). */
  forgetLocalChanges(): void {
    this.savedRevision = this.revision;
    discardDraft(this.id, this.storage);
  }

  dispose(): void {
    if (this.timer) clearTimeout(this.timer);
    if (typeof window !== "undefined") window.removeEventListener("online", this.online);
  }
}
